import { Injectable } from '@angular/core';
import {
  CommitteeItem,
  CommitteeItemInput,
  createCommitteeItem,
} from './committee-item';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  Subscription as RxSubscription,
} from 'rxjs';
import { Apollo, Subscription } from 'apollo-angular';
import { CurrentUserService } from '../../services/current-user.service';
import queries from './graphql/committee-items';
import { CommitteeApprovalType } from '../committee-approvals/committee-approval-type';
import { Mutable } from '../../types/mutable';
import { MatTableDataSource } from '@angular/material/table';
import { UserPermissionsService } from '../../services/user-permissions.service';
import { User } from '../../interfaces/user';
import Milestone from "../../interfaces/milestone";

type CommitteeItemsHash = {
  [key: string]: Mutable<CommitteeItem>[];
};
type ItemBehaviorHash = {
  [key: string]: BehaviorSubject<Mutable<CommitteeItem>[]>;
};
type CommitteeObservableHash = {
  [key: string]: Observable<Mutable<CommitteeItem>[]>;
};
@Injectable({
  providedIn: 'root',
})
export class CommitteeItemsService {
  private _committeeItems: Mutable<CommitteeItemsHash> = {};
  private committeeItems: ItemBehaviorHash = {};
  public approvalTypes: any;
  public user: User;
  private userSub: RxSubscription;
  committeeItems$: CommitteeObservableHash = {};
  constructor(
    private apollo: Apollo,
    private currentUser: CurrentUserService,
    private perms: UserPermissionsService
  ) {}
  addItemsFromApi(projectId: string, items: CommitteeItem[]) {
    this._committeeItems[projectId] = items.map((item) =>
      Object.assign({}, item)
    ) as Mutable<CommitteeItem>[];
    this.committeeItems[projectId] = new BehaviorSubject<
      Mutable<CommitteeItem>[]
    >(this._committeeItems[projectId]);
    this.committeeItems$[projectId] =
      this.committeeItems[projectId].asObservable();
    this.userSub = this.currentUser
      .getUser()
      .subscribe((user) => (this.user = user));
  }
  createNewCommitteeItem(item: CommitteeItem): Observable<any> {
    return this.apollo.mutate({
      mutation: queries.CREATE_COMMITTEE_ITEM,
      variables: {
        input: this.getInput(item),
      },
    });
  }
  updateCommitteeItem(item: CommitteeItem): Observable<any> {
    return this.apollo.mutate({
      mutation: queries.UPDATE_COMMITTEE_ITEM,
      variables: {
        id: item.id,
        input: this.getInput(item),
      },
    });
  }

  deleteCommitteeItem(projectId: string, item: CommitteeItem): void {
    item = { ...item, shouldDelete: true };
    if (item.id) {
      let obs = this.apollo.mutate({
        mutation: queries.DELETE_COMMITTEE_ITEM,
        variables: {
          id: item.id,
        },
      });
      let sub = obs.subscribe((data) => {
        this.updateItem(projectId, item);
        sub && sub.unsubscribe();
      });
    } else {
      this.updateItem(projectId, item, item.tempId);
    }
  }
  getInput(item: CommitteeItem): object {
    let input: CommitteeItemInput = {
      name: item.name,
      resolution: item.resolution,
      project_id: item.project_id,
      committee_approval_type_id: item.committee_approval_type_id,
      is_post_closing: item.is_post_closing,
      related_approval_type_id: item.related_approval_type_id,
      cleared: item.cleared ? item.cleared : false,
      is_legal_note: item.is_legal_note,
      is_condition: item.is_condition,
      milestone_id: item.milestone_id,
    };
    if (!item.id) {
      input.creator_id = this.user.id;
    } else {
      input.updater_id = this.user.id;
    }
    return input;
  }
  removeFromConditions(projectId: string, item: CommitteeItem) {
    let condition = { ...item };
    condition.is_condition = false;
    this.saveCommitteeItem(projectId, condition);
  }
  saveCommitteeItem(
    projectId: string,
    item: CommitteeItem,
    callback?: Function,
    milestoneId?: string
  ): void {
    let obs: Observable<any>;
    const pendingRelated = !item.id ? [...(item.relatedApprovalTypes ?? [])] : [];
    item.saving = true;
    this.updateItem(projectId, item, item.tempId, milestoneId);
    if(milestoneId!==undefined){
      item.milestone_id = milestoneId
    }
    if (!item.id) {
      obs = this.createNewCommitteeItem(item);
    } else {
      obs = this.updateCommitteeItem(item);
    }
    let sub = obs.subscribe((data) => {
      if (data.data.hasOwnProperty('item')) {
        if (!item.id && data.data.item?.id) {
          item.id = data.data.item.id;
          if (pendingRelated.length) {
            this.savePendingRelations(projectId, item, data.data.item, pendingRelated, callback);
            sub && sub.unsubscribe();
            return;
          }
        }
        this.updateItem(
          projectId,
          { ...data.data.item, saving: false },
          item.tempId
        );
      }
      if (callback) {
        callback();
      }
      sub && sub.unsubscribe();
    });
  }
  private savePendingRelations(
    projectId: string,
    item: CommitteeItem,
    savedItem: CommitteeItem,
    pendingRelated: CommitteeApprovalType[],
    callback?: Function
  ): void {
    this.updateItem(
      projectId,
      { ...savedItem, relatedApprovalTypes: pendingRelated, saving: true },
      item.tempId
    );
    let sub = forkJoin(
      pendingRelated.map((approval) => this.addItemRelatedApproval(item, approval.id))
    ).subscribe({
      next: (results: any[]) => {
        const relatedApprovalTypes = pendingRelated.map((approval, index) => ({
          ...approval,
          committeeItemRelationId: results[index]?.data?.item?.id,
        }));
        this.updateItem(
          projectId,
          { ...savedItem, relatedApprovalTypes, saving: false },
          item.tempId
        );
        if (callback) {
          callback();
        }
        sub && sub.unsubscribe();
      },
      error: () => {
        // The item itself saved; drop the unsaved departments so the row matches the server
        this.updateItem(
          projectId,
          { ...savedItem, saving: false },
          item.tempId
        );
        sub && sub.unsubscribe();
      },
    });
  }


  addItemRelatedApproval(item: CommitteeItem, related_approval_type_id:any){
    return this.apollo.mutate({
      mutation: queries.CREATE_COMMITTEE_ITEM_RELATION,
      variables: {
        committee_item_id: item.id,
        related_approval_type_id: related_approval_type_id
      },
    });
  }

  deleteItemRelatedApproval(id: string){
    return this.apollo.mutate({
      mutation: queries.DELETE_COMMITTEE_ITEM_RELATION,
      variables: {
        id: id
      }
    })
  }
  updateItem(projectId: string, item: CommitteeItem, tempId?: string, milestone?: string) {
    if (item.shouldDelete) {
      item.committee_approval_type_id = -1;
    }
    let items = [...this._committeeItems[projectId]];
    let index = items.findIndex((iItem) => item.id && iItem.id === item.id);
    if (index === -1) {
      index = items.findIndex((iItem) => iItem.tempId === tempId);
    }
    // if we still didn't find it
    if (index === -1) {
      items = [...items, item];
    } else {
      if (item.shouldDelete) {
        items = [...items.slice(0, index), ...items.slice(index + 1)];
      } else {
        items[index] = { ...items[index], ...item };
      }
    }
    this._committeeItems[projectId] = [...items];
    this.committeeItems[projectId].next(this._committeeItems[projectId]);
  }

  copyItemToConditions(projectId: string, item: CommitteeItem) {
    let condition = { ...item };
    condition.is_condition = true;
    this.saveCommitteeItem(projectId, condition);
  }
  getLegalApprovalType(): CommitteeApprovalType | undefined {
    return this.approvalTypes?.find(
      (approvalType: CommitteeApprovalType) => approvalType.name === 'Legal'
    );
  }

  addItem(
    projectId: string,
    approvalTypeId: number,
    isLegalNote?: boolean,
    isCondition?: boolean,
  ) {
    this._committeeItems[projectId] = [
      ...this._committeeItems[projectId],
      createCommitteeItem({
        approvalTypeId,
        projectId,
        isLegalNote,
        isCondition,
      })
    ];
    this.committeeItems[projectId].next(this._committeeItems[projectId]);
  }
}
